import React, { useState, useEffect, useMemo } from 'react'
import { Spin, Input, Checkbox, Pagination } from 'antd'
import { COLORS } from 'theme'
import {
  MenuFlex,
  StyledTab,
  SubmitFlex,
  Wrapper,
} from 'modules/Dashboard/Partials/UsersFilter/Styles/style'
import VirtualizedCheckboxGroup from 'components/UIComponents/Filter'
import { SearchOutlined } from '@ant-design/icons'
import FilterIcon from '../../../../components/icons/FilterIcon'
import ArrowDropDown from '../../../../components/icons/ArrowDropDown'
import Button from 'components/Button'
import { StateCityMessage } from 'modules/Systemconfig/Partials/TableSettings/Style/style'
import CheckIconWhite from 'components/icons/CheckIconWhite'
import FilterButtonComponent from 'components/FilterButton'
import { FilterContainer, FilterPopup } from 'components/FilterButton/styles'
import ProgressBar from 'components/ProgressBar'
import { StatusBadge, UsageText } from 'components/ProgressBar/styles'
import CollegeCard from 'components/CollegeCard'
import CheckboxGroup from 'components/CheckboxGroup'
import RadioButtonGroup from 'components/RadioButtonGroup'

import {
  SearchSection,
  ShowingText,
  Overlay,
  TableTop,
  CustomStyledTable,
} from '../../Style/style'

const getSubscriptionStatus = record => {
  const { subscription_type, tokens_used, token_limit, end_date } = record
  const currentDate = new Date()
  const endDate = new Date(end_date)
  const oneMonthFromNow = new Date()
  oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1)

  // Common conditions for both trial and regular subscriptions
  if (tokens_used >= token_limit || endDate < currentDate) {
    return subscription_type === 'trial' ? 'trial-expired' : 'expired'
  }

  if (endDate < oneMonthFromNow) {
    return subscription_type === 'trial' ? 'trial-expiring' : 'expiring-soon'
  }

  return subscription_type === 'trial' ? 'trial' : 'active'
}

const getStatusText = status => {
  switch (status) {
    case 'expired':
      return 'Expired'
    case 'active':
      return 'Active'
    case 'trial':
      return 'In Trial Period'
    case 'trial-expired':
      return 'Trial Period Expired'
    case 'expiring-soon':
      return 'Expiring Soon'
    case 'trial-expiring':
      return 'Trial Period Expiring Soon'
    default:
      return status
  }
}

const assessmentTypes = [
  { label: 'Communication Assessment', value: 'communication' },
  { label: 'Behavioral Assessment', value: 'behavior' },
  { label: 'Cognitive Aptitude', value: 'cognitive' },
  { label: 'Technical Assessment - MCQ', value: 'tech_mcq' },
  { label: 'Technical Assessment - Coding', value: 'tech_coding' },
  { label: 'Role Based Assessment', value: 'role_based' },
]

const columns = [
  {
    title: 'STATUS AND USED VS TAKEN',
    dataIndex: 'status',
    key: 'status',
    width: '25%',
    render: (text, record) => {
      const status = getSubscriptionStatus(record)
      return (
        <div style={{ width: '100%' }}>
          <ProgressBar
            value={record.tokens_used}
            total={record.token_limit}
            status={status}
          >
            <StatusBadge status={status}>{getStatusText(status)}</StatusBadge>
            <UsageText>
              {record.tokens_used} / {record.token_limit}
            </UsageText>
          </ProgressBar>
        </div>
      )
    },
  },
  {
    title: 'COLLEGE NAME',
    dataIndex: 'campus_name',
    key: 'campus_name',
    width: '25%',
    sorter: true,
    render: (text, record) => (
      <CollegeCard
        name={record.campus_name}
        city={record.city}
        state={record.state}
        logoUrl={record.campus_logo_url}
      />
    ),
  },
  {
    title: 'CONTACT DETAILS',
    dataIndex: 'contact_number',
    key: 'contactPhone',
    width: '20%',
    render: (text, record) => (
      <div>
        <div>
          {record.contact_number}
          <i className="far fa-copy" />
        </div>
        <div>
          {record.contact_email}
          <i className="far fa-copy" />
        </div>
      </div>
    ),
  },
  {
    title: 'DEGREE & DEPARTMENT',
    dataIndex: 'degreeDepartment',
    key: 'degrees',
    width: '20%',
    render: (degreeDepartment, record) => {
      // Use degreeDepartment if available, otherwise fall back to degrees
      const deptData = degreeDepartment || record.degrees || []

      return (
        <div>
          {deptData.slice(0, 2).map((degree, index) => (
            <div key={index}>
              {degree.short_form} - {degree.stream_name}
            </div>
          ))}

          {/* Show the "more" indicator if hasMoreDegreeDepartments is true */}
          {record.hasMoreDegreeDepartments && (
            <div style={{ fontWeight: 'bold' }}>
              + {record.degreeDepartmentCount - 2} more
            </div>
          )}
        </div>
      )
    },
  },
  {
    title: 'START & END DATE',
    dataIndex: 'start_date',
    key: 'start_date',
    width: '15%',
    render: (text, record) => (
      <div>
        {new Date(record.start_date).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })}
        {' to '}
        {new Date(record.end_date).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })}
      </div>
    ),
  },
]

const SubscriptionRenewalCollegeTable = ({
  instituteList,
  page,
  limit,
  loading,
  filters,
  metrics,
  onPageChange,
  onFilterChange,
  currentCount,
  stateCityPairs = [],
}) => {
  const [showFilters, setShowFilters] = useState(false)
  const [selectedAssessmentType, setSelectedAssessmentType] = useState(
    filters.type_name || 'communication'
  )
  const [trialPeriod, setTrialPeriod] = useState(filters.isTrial || false)
  const [subscription, setSubscription] = useState(
    filters.isSubscribed || false
  )
  const [searchValue, setSearchValue] = useState(filters.searchBy || '')

  const { states, citiesByState } = useMemo(() => {
    const statesSet = new Set()
    const cityMap = new Map()

    stateCityPairs.forEach(([state, city]) => {
      statesSet.add(state)
      if (!cityMap.has(state)) {
        cityMap.set(state, new Set())
      }
      cityMap.get(state).add(city)
    })

    // Convert to array format needed by VirtualizedCheckboxGroup
    const statesList = Array.from(statesSet).map(state => ({
      state,
      stateId: state,
    }))

    // Convert city sets to arrays
    const citiesMap = {}
    cityMap.forEach((cities, state) => {
      citiesMap[state] = Array.from(cities).map(city => ({
        city,
        cityId: city,
      }))
    })

    return {
      states: statesList,
      citiesByState: citiesMap,
    }
  }, [stateCityPairs])

  // Initialize with default assessment type if not set
  useEffect(() => {
    if (!filters.type_name) {
      handleFilterChange('type_name', 'communication')
    }
  }, [])

  // Update local state when filters change from parent
  useEffect(() => {
    setSelectedAssessmentType(filters.type_name || 'communication')
    setTrialPeriod(filters.isTrial || false)
    setSubscription(filters.isSubscribed || false)
    setSearchValue(filters.searchBy || '')
    setStateNames(filters.states || [])
    setCityName(filters.cities || [])
  }, [filters])

  const [stateId, setStateId] = useState([])
  const [cityId, setCityId] = useState([])
  const [stateList, setStateList] = useState([])
  const [cityList, setCityList] = useState([])
  const [stateNames, setStateNames] = useState(filters.states || [])
  const [cityName, setCityName] = useState(filters.cities || [])
  const [degreeSearch, setDegreeSearch] = useState('')
  const [check, setCheck] = useState(false)
  const [sortField, setSortField] = useState(filters.order || 'campus_name')
  const [sortDirection, setSortDirection] = useState(filters.sort || 'asc')

  // Reset filter selections when assessment type changes
  useEffect(() => {
    setStateId([])
    setStateNames([])
    setCityId([])
    setCityName([])
    setCityList([])
  }, [selectedAssessmentType])

  // Update states list from stateCityPairs data
  useEffect(() => {
    setStateList(states)
  }, [states])

  // Update cities when state selection changes
  useEffect(() => {
    if (stateId?.length) {
      const availableCities = stateId.flatMap(
        state => citiesByState[state] || []
      )
      setCityList(availableCities)
    } else {
      setCityList([])
    }
  }, [stateId, citiesByState])

  // Reset city selection when state changes
  useEffect(() => {
    setCityId([])
    setCityName([])
  }, [stateId])

  const submitFilter = () => {
    setShowFilters(false)

    // Reset to first page when applying new filters
    const newFilters = {
      ...filters,
      states: stateNames || [],
      cities: cityName || [],
      pageNo: 0, // Reset to first page
      updateFilters: true, // Signal that filters are being updated
    }

    // Apply filters and fetch new data
    onFilterChange(newFilters)
  }

  const handleFilterChange = (key, value) => {
    const newFilters = {
      ...filters,
      ...(typeof key === 'object' ? key : { [key]: value }),
      updateFilters: true, // Signal that filters are being updated
    }

    // Reset to first page when filters change
    if (typeof key === 'object' || key !== 'pageNo') {
      newFilters.pageNo = 0
    }

    onFilterChange(newFilters)
  }

  const handleSearch = e => {
    const value = e.target.value
    setSearchValue(value)
    handleFilterChange('searchBy', value)
  }

  const handleTableChange = (pagination, tableFilters, sorter) => {
    if (sorter && sorter.field) {
      const sortOrder = sorter.order === 'descend' ? 'desc' : 'asc'
      setSortField(sorter.field)
      setSortDirection(sortOrder)

      handleFilterChange({
        sort: sortOrder,
        order: sorter.field,
      })
    }
  }

  const handlePageChange = newPage => {
    onPageChange(newPage - 1, {
      ...filters,
      pageOnly: true,
    })
  }

  const menu = (
    <MenuFlex column>
      <StyledTab
        border={true}
        tabPosition={'left'}
        type="card"
        padding="15px"
        popupClassName="filter"
        items={[
          {
            label: 'State',
            key: '1',
            children: (
              <VirtualizedCheckboxGroup
                options={states}
                value={stateId}
                setValue={setStateId}
                valueParam="state"
                labelParam="state"
                setStateName={setStateNames}
                filterId={true}
                stateName={stateNames}
                degreeSearch={degreeSearch}
                setDegreeSearch={setDegreeSearch}
                check={check}
              />
            ),
          },
          {
            label: 'City',
            key: '2',
            children: (
              <>
                {stateId?.length ? (
                  <VirtualizedCheckboxGroup
                    options={cityList}
                    value={cityId}
                    setValue={setCityId}
                    valueParam="city"
                    labelParam="city"
                    filterId={true}
                    setStateName={setCityName}
                    stateName={cityName}
                    degreeSearch={degreeSearch}
                    setDegreeSearch={setDegreeSearch}
                    check={check}
                  />
                ) : (
                  <StateCityMessage>Please select state first</StateCityMessage>
                )}
              </>
            ),
          },
        ]}
      />
      <SubmitFlex right>
        <Button.Secondary text="Cancel" onClick={() => setShowFilters(false)} />
        <Button.Primary
          onClick={submitFilter}
          text="Apply Filter"
          icon={<CheckIconWhite />}
        />
      </SubmitFlex>
    </MenuFlex>
  )

  const filteredData = instituteList || []
  const showingCount = Math.min((page + 1) * limit, currentCount || 0)

  return (
    <>
      {/* Assessment Type Selection */}
      <RadioButtonGroup
        value={selectedAssessmentType}
        options={assessmentTypes}
        onChange={e => {
          const newValue = e.target.value
          setSelectedAssessmentType(newValue)

          // Batch all changes together
          const newFilters = {
            ...filters,
            type_name: newValue,
            states: [],
            cities: [],
            pageNo: 0,
            updateFilters: true,
          }
          onFilterChange(newFilters)
        }}
      />

      {/* Subscription Type Selection */}
      <CheckboxGroup.Styled
        options={[
          {
            label: `Trial Period (${metrics?.trialCount || 0})`,
            value: 'trial',
          },
          {
            label: `Subscription (${metrics?.subscribedCount || 0})`,
            value: 'subscription',
          },
        ]}
        value={[
          ...(trialPeriod ? ['trial'] : []),
          ...(subscription ? ['subscription'] : []),
        ]}
        onChange={values => {
          const isTrial = values.includes('trial')
          const isSubscribed = values.includes('subscription')

          // Update local state
          setTrialPeriod(isTrial)
          setSubscription(isSubscribed)

          // Update filters
          handleFilterChange({
            isTrial,
            isSubscribed,
          })
        }}
      />

      <TableTop>
        {/* Search Bar */}
        <SearchSection>
          <Input
            placeholder="Search by keyword"
            prefix={<SearchOutlined style={{ color: COLORS.GRAY_LIGHT }} />}
            value={searchValue}
            onChange={handleSearch}
          />
        </SearchSection>

        {/* Filter Button */}
        <FilterContainer>
          <FilterButtonComponent onClick={() => setShowFilters(!showFilters)}>
            <FilterIcon />
            <span>Filter</span>
            <ArrowDropDown />
          </FilterButtonComponent>
          {showFilters && (
            <>
              <FilterPopup>{menu}</FilterPopup>
              <Overlay onClick={() => setShowFilters(false)} />
            </>
          )}
        </FilterContainer>

        {/* Count Indicator */}
        <ShowingText>
          Showing {showingCount} of {currentCount}
        </ShowingText>
      </TableTop>

      {/* Data Table */}
      <Spin spinning={loading}>
        <CustomStyledTable
          columns={columns}
          dataSource={filteredData}
          pagination={false}
          rowKey="subscription_id"
          onChange={handleTableChange}
        />
      </Spin>

      {/* Pagination */}
      {currentCount > 0 && (
        <div
          style={{
            margin: '16px',
            textAlign: 'center',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Pagination
            current={parseInt(page) + 1}
            total={currentCount}
            pageSize={parseInt(limit)}
            onChange={handlePageChange}
          />
        </div>
      )}
    </>
  )
}

export default SubscriptionRenewalCollegeTable
